import React from 'react';
import { BadgeProps } from '@/types';
import Chip from '@mui/material/Chip';

type AppBadgeProps = BadgeProps & {
  /**
   * 'default' (the original behavior) renders a plain Tailwind-styled
   * <span> — used by every existing screen. 'mui' renders through real
   * MUI Chip (theme tokens via sx) instead — for screens built with the
   * MUI-first component set (see LoginPage.tsx). Same dual-mode trick
   * AppButton.tsx/AppCard.tsx/Input.tsx already use.
   */
  engine?: 'default' | 'mui';
};

const TAILWIND_VARIANTS: Record<NonNullable<BadgeProps['variant']>, string> = {
  default: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
  secondary: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
  primary: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-400 dark:border-indigo-800',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800',
  warning: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800',
  error: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-800',
  info: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800',
};

const MUI_VARIANT_COLOR: Record<NonNullable<BadgeProps['variant']>, { bg: string; color: string }> = {
  default: { bg: '#F1F5F9', color: '#334155' },
  secondary: { bg: '#F3F4F6', color: '#374151' },
  primary: { bg: '#EEF2FF', color: '#4338CA' },
  success: { bg: '#ECFDF5', color: '#047857' },
  warning: { bg: '#FFFBEB', color: '#B45309' },
  error: { bg: '#FFF1F2', color: '#BE123C' },
  info: { bg: '#EFF6FF', color: '#1D4ED8' },
};

const Badge: React.FC<AppBadgeProps> = ({ children, variant = 'default', className = '', engine = 'default' }) => {
  if (engine === 'mui') {
    const { bg, color } = MUI_VARIANT_COLOR[variant];
    return (
      <Chip
        label={children}
        className={className}
        size="small"
        sx={{
          bgcolor: bg,
          color,
          fontWeight: 600,
          fontSize: '0.75rem',
          borderRadius: '999px', // fully rounded pill
          height: 22,
        }}
      />
    );
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${TAILWIND_VARIANTS[variant]} ${className}`}>
      {children}
    </span>
  );
};

export default Badge;
